import {
  isFixedRateLoanProductFields,
  isFixedRateLoanProductRow,
  isFloatingRateLoanProductFields,
  isFloatingRateLoanProductRow,
  isForwardProductFields,
  isForwardProductRow,
  isOptionProductFields,
  isOptionProductRow,
  isRepoProductFields,
  isRepoProductRow,
} from '@src/types/OTC/otcGuards';
import type { DealProductBuilderRow, OTCFieldsData } from '@src/types/OTC/otcProductBuilderTypes';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { FixedRateLoanProductBuilderSliceStrategy } from './FixedRateLoanProductBuilderSliceStrategy';
import { FloatingRateLoanProductBuilderSliceStrategy } from './FloatingRateLoanProductBuilderSliceStrategy';
import { ForwardProductBuilderSliceStrategy } from './ForwardProductBuilderSliceStrategy';
import { OptionProductBuilderSliceStrategy } from './OptionProductBuilderSliceStrategy';
import { RepoProductBuilderSliceStrategy } from './RepoProductBuilderSliceStrategy';

/** Helper function to get the OTC Subtype slice strategy for a row */
export function getSliceStrategyForRow(currentRow: DealProductBuilderRow): {
  sliceStrategy: BaseProductBuilderSliceStrategy<DealProductBuilderRow>;
  currentRow: DealProductBuilderRow;
} {
  if (isForwardProductRow(currentRow)) {
    return {
      sliceStrategy: ForwardProductBuilderSliceStrategy,
      currentRow: currentRow,
    };
  }

  if (isFixedRateLoanProductRow(currentRow)) {
    return {
      sliceStrategy: FixedRateLoanProductBuilderSliceStrategy,
      currentRow: currentRow,
    };
  }

  if (isOptionProductRow(currentRow)) {
    return {
      sliceStrategy: OptionProductBuilderSliceStrategy,
      currentRow: currentRow,
    };
  }

  if (isFloatingRateLoanProductRow(currentRow)) {
    return {
      sliceStrategy: FloatingRateLoanProductBuilderSliceStrategy,
      currentRow: currentRow,
    };
  }

  if (isRepoProductRow(currentRow)) {
    return {
      sliceStrategy: RepoProductBuilderSliceStrategy,
      currentRow: currentRow,
    };
  }

  // this should never happen typewise (currentRow is a never, but just for safety)
  throw new Error(`Unsupported product type`, {
    cause: `CurrentRow: ${JSON.stringify(currentRow)}`,
  });
}

/** Helper function to get the OTC Fields Dialog Subtype slice strategy for a row */
export function getSliceStrategyForStateWithOTCFields(otcFields: OTCFieldsData): {
  sliceStrategy: BaseProductBuilderSliceStrategy<DealProductBuilderRow>;
  otcFields: OTCFieldsData;
} {
  const productType = otcFields.productType;
  if (isForwardProductFields(otcFields)) {
    return {
      sliceStrategy: ForwardProductBuilderSliceStrategy,
      otcFields: otcFields,
    };
  }

  if (isFixedRateLoanProductFields(otcFields)) {
    return {
      sliceStrategy: FixedRateLoanProductBuilderSliceStrategy,
      otcFields: otcFields,
    };
  }

  if (isOptionProductFields(otcFields)) {
    return {
      sliceStrategy: OptionProductBuilderSliceStrategy,
      otcFields: otcFields,
    };
  }

  if (isFloatingRateLoanProductFields(otcFields)) {
    return {
      sliceStrategy: FloatingRateLoanProductBuilderSliceStrategy,
      otcFields: otcFields,
    };
  }

  if (isRepoProductFields(otcFields)) {
    return {
      sliceStrategy: RepoProductBuilderSliceStrategy,
      otcFields: otcFields,
    };
  }

  throw new Error(`Unsupported product type: ${productType}`);
}

/** Helper function to get the Slice Strategy functions and typed row data for operations on the row data */
export function getSliceStrategyForRowActions(
  existingData: DealProductBuilderRow,
  newData: DealProductBuilderRow
): {
  sliceStrategy: BaseProductBuilderSliceStrategy<DealProductBuilderRow>;
  typedExistingData: DealProductBuilderRow;
  typedNewData: DealProductBuilderRow;
} {
  // Validate that both rows have the same product type
  if (existingData.product !== newData.product) {
    throw new Error(`Product type mismatch: currentRow has '${existingData.product}', data has '${newData.product}'`);
  }

  // Type guard and return appropriate slice support based on product type
  if (isForwardProductRow(existingData) && isForwardProductRow(newData)) {
    return {
      sliceStrategy: ForwardProductBuilderSliceStrategy,
      typedExistingData: existingData,
      typedNewData: newData,
    };
  }

  if (isFixedRateLoanProductRow(existingData) && isFixedRateLoanProductRow(newData)) {
    return {
      sliceStrategy: FixedRateLoanProductBuilderSliceStrategy,
      typedExistingData: existingData,
      typedNewData: newData,
    };
  }

  if (isOptionProductRow(existingData) && isOptionProductRow(newData)) {
    return {
      sliceStrategy: OptionProductBuilderSliceStrategy,
      typedExistingData: existingData,
      typedNewData: newData,
    };
  }

  if (isFloatingRateLoanProductRow(existingData) && isFloatingRateLoanProductRow(newData)) {
    return {
      sliceStrategy: FloatingRateLoanProductBuilderSliceStrategy,
      typedExistingData: existingData,
      typedNewData: newData,
    };
  }

  if (isRepoProductRow(existingData) && isRepoProductRow(newData)) {
    return {
      sliceStrategy: RepoProductBuilderSliceStrategy,
      typedExistingData: existingData,
      typedNewData: newData,
    };
  }

  throw new Error(`Unsupported product type: ${existingData.product}`);
}

export function getSliceStrategyForOTCFields(
  currentRow: DealProductBuilderRow,
  otcFields: OTCFieldsData
): {
  sliceStrategy: BaseProductBuilderSliceStrategy<DealProductBuilderRow>;
  typedCurrentRow: DealProductBuilderRow;
  typedOtcFields: OTCFieldsData;
} {
  // Type guard and return appropriate slice support based on product type
  if (isForwardProductRow(currentRow) && isForwardProductFields(otcFields)) {
    return {
      sliceStrategy: ForwardProductBuilderSliceStrategy,
      typedCurrentRow: currentRow,
      typedOtcFields: otcFields,
    };
  }

  if (isFixedRateLoanProductRow(currentRow) && isFixedRateLoanProductFields(otcFields)) {
    return {
      sliceStrategy: FixedRateLoanProductBuilderSliceStrategy,
      typedCurrentRow: currentRow,
      typedOtcFields: otcFields,
    };
  }

  if (isOptionProductRow(currentRow) && isOptionProductFields(otcFields)) {
    return {
      sliceStrategy: OptionProductBuilderSliceStrategy,
      typedCurrentRow: currentRow,
      typedOtcFields: otcFields,
    };
  }

  if (isFloatingRateLoanProductRow(currentRow) && isFloatingRateLoanProductFields(otcFields)) {
    return {
      sliceStrategy: FloatingRateLoanProductBuilderSliceStrategy,
      typedCurrentRow: currentRow,
      typedOtcFields: otcFields,
    };
  }

  if (isRepoProductRow(currentRow) && isRepoProductFields(otcFields)) {
    return {
      sliceStrategy: RepoProductBuilderSliceStrategy,
      typedCurrentRow: currentRow,
      typedOtcFields: otcFields,
    };
  }

  throw new Error(`Unsupported product type: ${currentRow.product}`);
}
